<template>
  <div
    class="track-query"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchCancel"
  >
    <!-- 列表视图 -->
    <template v-if="!selectedTrack">
      <!-- 下拉刷新提示 -->
      <div
        class="ptr-indicator"
        :class="{ animating: !ptrDragging }"
        :style="{ height: pullDistance + 'px' }"
        v-show="pullDistance > 0"
      >
        <span v-if="ptrState === 'loading'" class="ptr-spinner"></span>
        <span v-else class="ptr-arrow" :class="{ ready: ptrState === 'ready' }">↓</span>
        <span class="ptr-text">{{ ptrText }}</span>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar">
        <div class="search-input">
          <span class="search-icon">🔍</span>
          <input
            v-model="searchText"
            placeholder="搜索运单号/出库单号..."
            @keyup.enter="onSearch"
          />
          <span class="clear-icon" v-if="searchText" @click="searchText = ''">✕</span>
        </div>
      </div>

      <!-- 快捷日期筛选 -->
      <div class="date-filter">
        <div
          v-for="opt in dateOptions"
          :key="opt.key"
          class="date-tag"
          :class="{ active: dateRange === opt.key }"
          @click="switchDateRange(opt.key)"
        >
          {{ opt.label }}
        </div>
        <div
          class="date-tag custom"
          :class="{ active: dateRange === 'custom' }"
          @click="toggleCustomRange"
        >
          自定义
        </div>
      </div>

      <!-- 自定义期间面板 -->
      <div class="custom-range" v-if="showCustomRange">
        <div class="range-inputs">
          <input type="date" v-model="customStart" />
          <span class="range-sep">至</span>
          <input type="date" v-model="customEnd" />
        </div>
        <div class="range-actions">
          <span class="range-btn" @click="cancelCustomRange">收起</span>
          <span class="range-btn primary" @click="applyCustomRange">查询</span>
        </div>
      </div>

      <!-- 状态筛选标签 -->
      <div class="status-filter">
        <div
          class="filter-tag"
          :class="{ active: pickupStatus === 'all' }"
          @click="switchStatus('all')"
        >
          全部 {{ totalCount }}
        </div>
        <div
          class="filter-tag picked"
          :class="{ active: pickupStatus === 'picked' }"
          @click="switchStatus('picked')"
        >
          已揽收 {{ pickedCount }}
        </div>
        <div
          class="filter-tag pending"
          :class="{ active: pickupStatus === 'pending' }"
          @click="switchStatus('pending')"
        >
          未揽收 {{ pendingCount }}
        </div>
      </div>

      <!-- 提示文字 -->
      <div class="tip-text">
        {{ dateRange === 'custom'
          ? `自定义期间：${customStart} 至 ${customEnd}`
          : '默认展示当天申请的快递，支持下拉刷新与自定义期间查询' }}
      </div>

      <!-- 加载中 -->
      <div class="loading" v-if="loading">
        <div class="loading-spinner"></div>
        <div>加载中...</div>
      </div>

      <!-- 快递卡片列表 -->
      <div class="card-list" v-else>
        <div v-if="displayList.length === 0" class="empty">
          <div class="empty-icon">📦</div>
          <div class="empty-text">暂无快递数据</div>
        </div>

        <div
          v-for="item in displayList"
          :key="item.id"
          class="track-card"
          :class="getCardClass(item)"
          @click="showDetail(item)"
        >
          <!-- 卡片头部 -->
          <div class="card-header">
            <div class="bill-no">
              <span class="copy-icon" @click.stop="copyBillNo(item.carryBillNo)">📋</span>
              {{ item.carryBillNo }}
            </div>
            <div class="com-name-wrap">
              <span class="source-tag" :class="item.sourceType === 1 ? 'stk' : 'sal'">{{ item.sourceTypeName || '销售出库' }}</span>
              <span class="com-tag" :class="getComClass(item.logComCode)">{{ item.logComName }}</span>
            </div>
          </div>

          <!-- 卡片主体 -->
          <div class="card-body">
            <div class="status-row">
              <span class="status-badge" :class="getStatusBadgeClass(item)">
                {{ item.statusName }}
              </span>
              <span class="update-time" v-if="item.updateTime">
                {{ formatTime(item.updateTime) }}
              </span>
            </div>

            <div class="remark" v-if="item.remark">{{ item.remark }}</div>

            <div class="bill-info">
              <span class="label">单号：</span>
              <span class="value">{{ item.billNo }}</span>
            </div>

            <div class="phone-info" v-if="item.phone">
              <span class="label">寄件人电话：</span>
              <span class="value">{{ item.phone }}</span>
            </div>

            <div class="sender-info" v-if="item.senderName">
              <span class="label">寄件人：</span>
              <span class="value">{{ item.senderName }}</span>
            </div>
          </div>

          <!-- 卡片底部 -->
          <div class="card-footer">
            <span class="view-detail">查看详情 ></span>
          </div>
        </div>
      </div>
    </template>

    <!-- 详情视图 -->
    <MobileTrackDetail
      v-else
      :track-info="selectedTrack"
      @back="backToList"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { apiUrl } from '../api/config'
import MobileTrackDetail from './MobileTrackDetail.vue'

export default {
  name: 'MobileTrackQuery',
  components: {
    MobileTrackDetail
  },
  setup() {
    const loading = ref(false)
    const searchText = ref('')
    const pickupStatus = ref('all')
    const trackList = ref([])
    const selectedTrack = ref(null)
    const dateRange = ref('today')
    const showCustomRange = ref(false)
    const customStart = ref('')
    const customEnd = ref('')

    // 快捷日期选项：当天/昨天/近3天/近7天（按快递申请日期过滤）
    const dateOptions = [
      { key: 'today', label: '当天', days: 1 },
      { key: 'yesterday', label: '昨天', days: 0, offset: -1 },
      { key: 'last3', label: '近3天', days: 3 },
      { key: 'last7', label: '近7天', days: 7 }
    ]

    // 格式化为 yyyy-MM-dd
    const formatYmd = (date) => {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }

    // 计算当前选项的日期范围（yyyy-MM-dd，含首尾）
    const getDateRange = () => {
      if (dateRange.value === 'custom' && customStart.value && customEnd.value) {
        return { start: customStart.value, end: customEnd.value }
      }
      const opt = dateOptions.find(x => x.key === dateRange.value) || dateOptions[0]
      const today = new Date()
      if (opt.key === 'yesterday') {
        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)
        return { start: formatYmd(yesterday), end: formatYmd(yesterday) }
      }
      const start = new Date(today)
      start.setDate(today.getDate() - (opt.days - 1))
      return { start: formatYmd(start), end: formatYmd(today) }
    }

    // 切换自定义期间面板（首次打开预填最近7天）
    const toggleCustomRange = () => {
      showCustomRange.value = !showCustomRange.value
      if (showCustomRange.value && !customStart.value && !customEnd.value) {
        const end = new Date()
        const start = new Date()
        start.setDate(end.getDate() - 6)
        customStart.value = formatYmd(start)
        customEnd.value = formatYmd(end)
      }
    }

    // 应用自定义期间
    const applyCustomRange = () => {
      if (!customStart.value || !customEnd.value) {
        ElMessage.warning('请选择开始和结束日期')
        return
      }
      // yyyy-MM-dd 字符串比较等价于日期比较，起止颠倒时自动交换
      if (customStart.value > customEnd.value) {
        [customStart.value, customEnd.value] = [customEnd.value, customStart.value]
      }
      // 校验时间跨度不超过7天
      const startDate = new Date(customStart.value)
      const endDate = new Date(customEnd.value)
      const diffTime = endDate - startDate
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      if (diffDays > 6) {
        ElMessage.warning('时间跨度不能超过7天')
        return
      }
      dateRange.value = 'custom'
      showCustomRange.value = false
      loadData()
    }

    const cancelCustomRange = () => {
      showCustomRange.value = false
    }

    // ===== 下拉刷新 =====
    const PTR_THRESHOLD = 50
    const PTR_MAX_DISTANCE = 70
    const ptrState = ref('idle') // idle=未触发 pulling=下拉中 ready=可释放 loading=刷新中
    const ptrDragging = ref(false)
    const pullDistance = ref(0)
    let ptrStartY = 0
    let ptrEngaged = false
    let ptrAtTop = false

    const ptrText = computed(() => {
      if (ptrState.value === 'ready') return '释放立即刷新'
      if (ptrState.value === 'loading') return '正在刷新...'
      return '下拉刷新'
    })

    // 查找最近的滚动祖先容器（MobileHome 的 .content）
    const findScrollParent = (el) => {
      let node = el && el.parentElement
      while (node && node !== document.documentElement) {
        const style = window.getComputedStyle(node)
        if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
          return node
        }
        node = node.parentElement
      }
      return null
    }

    const onTouchStart = (e) => {
      if (selectedTrack.value || loading.value || ptrState.value === 'loading') {
        ptrEngaged = false
        return
      }
      ptrStartY = e.touches[0].clientY
      const scroller = findScrollParent(e.currentTarget)
      ptrAtTop = scroller
        ? scroller.scrollTop <= 0
        : (document.scrollingElement || document.documentElement).scrollTop <= 0
      ptrEngaged = false
    }

    const onTouchMove = (e) => {
      if (!ptrAtTop || selectedTrack.value || ptrState.value === 'loading') return
      const deltaY = e.touches[0].clientY - ptrStartY
      if (!ptrEngaged) {
        if (deltaY > 8) {
          ptrEngaged = true
          ptrDragging.value = true
        } else {
          return
        }
      }
      // 阻止页面滚动与浏览器原生下拉刷新
      e.preventDefault()
      if (deltaY <= 0) {
        pullDistance.value = 0
        ptrState.value = 'idle'
        return
      }
      pullDistance.value = Math.min(deltaY * 0.45, PTR_MAX_DISTANCE)
      ptrState.value = pullDistance.value >= PTR_THRESHOLD ? 'ready' : 'pulling'
    }

    const onTouchEnd = () => {
      ptrDragging.value = false
      if (!ptrEngaged) return
      ptrEngaged = false
      if (ptrState.value === 'ready') {
        ptrState.value = 'loading'
        pullDistance.value = 50
        // 静默刷新：保住列表内容，仅显示顶部刷新指示
        loadData({ silent: true }).finally(() => {
          ptrState.value = 'idle'
          pullDistance.value = 0
        })
      } else {
        pullDistance.value = 0
        ptrState.value = 'idle'
      }
    }

    const onTouchCancel = () => {
      ptrDragging.value = false
      ptrEngaged = false
      pullDistance.value = 0
      ptrState.value = 'idle'
    }

    // 统计数据（基于期间内全量数据，不受状态页签影响；归类依据后端 state 字段）
    const totalCount = computed(() => trackList.value.length)
    const pickedCount = computed(() => trackList.value.filter(isPicked).length)
    const pendingCount = computed(() => trackList.value.filter(x => !isPicked(x)).length)

    // 按当前状态页签本地过滤的显示列表（数据一次性全量获取，切换页签不重新请求）
    const displayList = computed(() => {
      if (pickupStatus.value === 'picked') {
        return trackList.value.filter(isPicked)
      }
      if (pickupStatus.value === 'pending') {
        return trackList.value.filter(x => !isPicked(x))
      }
      return trackList.value
    })

    // 获取认证头
    const getAuthHeaders = () => {
      const token = localStorage.getItem('token')
      const headers = {
        'Accept': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      return headers
    }

    // 加载数据（silent=true 时不显示整屏loading，用于下拉刷新）
    const loadData = async ({ silent = false } = {}) => {
      if (!silent) {
        loading.value = true
      }
      try {
        const range = getDateRange()
        const params = new URLSearchParams({
          pickupStatus: 'all',
          search: searchText.value,
          startDate: range.start,
          endDate: range.end
        })

        const url = apiUrl(`/Api/Express/TodayList?${params.toString()}`)
        const res = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders()
        })

        if (res.status === 401) {
          localStorage.removeItem('token')
          localStorage.removeItem('userName')
          localStorage.removeItem('expireAt')
          localStorage.removeItem('isAdmin')
          window.location.reload()
          return
        }

        if (!res.ok) {
          throw new Error('查询失败')
        }

        const json = await res.json()
        trackList.value = json.data || []
      } catch (e) {
        ElMessage.error(e?.message || '查询失败')
      } finally {
        if (!silent) {
          loading.value = false
        }
      }
    }

    // 切换状态（本地过滤，无需重新请求）
    const switchStatus = (status) => {
      pickupStatus.value = status
    }

    // 切换日期范围
    const switchDateRange = (key) => {
      if (dateRange.value === key) return
      dateRange.value = key
      loadData()
    }

    // 搜索
    const onSearch = () => {
      loadData()
    }

    // 复制单号
    const copyBillNo = (billNo) => {
      navigator.clipboard.writeText(billNo).then(() => {
        ElMessage.success('已复制')
      }).catch(() => {
        ElMessage.error('复制失败')
      })
    }

    // 显示详情
    const showDetail = (item) => {
      selectedTrack.value = item
      // 添加历史记录，这样按返回键会回到列表
      window.history.pushState({ showDetail: true }, '')
    }

    // 返回列表
    const backToList = () => {
      selectedTrack.value = null
      // 如果是通过浏览器返回触发的，不需要再push
      if (window.history.state?.showDetail) {
        window.history.back()
      }
    }

    // 监听浏览器返回按钮
    const handlePopState = () => {
      if (selectedTrack.value) {
        selectedTrack.value = null
      }
    }

    // ===== 状态样式映射（判定逻辑在后端 state 字段，前端只做映射不做判断） =====
    // state: signed=已签收, picked=已揽收, transit=运输中, error=疑难, pending=待揽收
    const STATE_CARD_CLASS = {
      signed: 'picked',
      picked: 'picked',
      transit: 'transit',
      error: 'error',
      pending: 'pending'
    }
    const STATE_BADGE_CLASS = {
      signed: 'success',
      picked: 'success',
      transit: 'info',
      error: 'danger',
      pending: 'warning'
    }
    // 已揽收页签包含的状态（其余归入未揽收页签）
    const PICKED_STATES = ['signed', 'picked', 'transit']

    const isPicked = (item) => PICKED_STATES.includes(item.state)
    const getCardClass = (item) => STATE_CARD_CLASS[item.state] || 'pending'
    const getStatusBadgeClass = (item) => STATE_BADGE_CLASS[item.state] || 'warning'

    // 快递公司配色：已知公司固定配色，未知公司按编码哈希取色
    const COM_COLOR_COUNT = 6
    const COM_KNOWN_COLORS = {
      shunfeng: 'c1',
      shunfengkuaiyun: 'c1',
      yuantong: 'c2',
      zhongtong: 'c3',
      yunda: 'c4',
      shentong: 'c5',
      jtexpress: 'c6',
      kuayue: 'c2',
      yimidida: 'c3',
      huolala: 'c4'
    }
    const getComClass = (code) => {
      if (!code) return 'c1'
      const key = String(code).toLowerCase()
      if (COM_KNOWN_COLORS[key]) return COM_KNOWN_COLORS[key]
      let hash = 0
      for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) % COM_COLOR_COUNT
      }
      return `c${hash + 1}`
    }

    // 格式化时间
    const formatTime = (time) => {
      if (!time) return ''
      const date = new Date(time)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${month}/${day} ${hours}:${minutes}`
    }

    onMounted(() => {
      loadData()
      window.addEventListener('popstate', handlePopState)
    })

    onUnmounted(() => {
      window.removeEventListener('popstate', handlePopState)
    })

    return {
      loading,
      searchText,
      pickupStatus,
      dateRange,
      dateOptions,
      trackList,
      selectedTrack,
      totalCount,
      pickedCount,
      pendingCount,
      displayList,
      showCustomRange,
      customStart,
      customEnd,
      ptrState,
      ptrDragging,
      pullDistance,
      ptrText,
      switchStatus,
      switchDateRange,
      toggleCustomRange,
      applyCustomRange,
      cancelCustomRange,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
      onSearch,
      copyBillNo,
      showDetail,
      backToList,
      getCardClass,
      getStatusBadgeClass,
      getComClass,
      formatTime
    }
  }
}
</script>

<style scoped>
.track-query {
  padding: 8px;
}

/* 搜索栏 */
.search-bar {
  margin-bottom: 8px;
}

.search-input {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 20px;
  padding: 6px 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.search-icon {
  font-size: 14px;
  margin-right: 6px;
}

.search-input input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  background: transparent;
}

.clear-icon {
  font-size: 12px;
  color: #999;
  cursor: pointer;
  margin-left: 6px;
}

/* 下拉刷新指示器 */
.ptr-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  overflow: hidden;
  color: #999;
  font-size: 12px;
}

.ptr-indicator.animating {
  transition: height 0.2s ease;
}

.ptr-arrow {
  display: inline-block;
  transition: transform 0.2s;
}

.ptr-arrow.ready {
  transform: rotate(180deg);
}

.ptr-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #e8e8e8;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* 快捷日期筛选 */
.date-filter {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.date-tag {
  flex: 1;
  text-align: center;
  padding: 5px 0;
  border-radius: 14px;
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.3s;
}

.date-tag.active {
  color: #fff;
  background: #1890ff;
}

/* 自定义期间面板 */
.custom-range {
  background: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-inputs input {
  flex: 1;
  min-width: 0;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  color: #333;
  outline: none;
}

.range-inputs input:focus {
  border-color: #1890ff;
}

.range-sep {
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
}

.range-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.range-btn {
  padding: 5px 18px;
  border-radius: 14px;
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  cursor: pointer;
}

.range-btn.primary {
  color: #fff;
  background: #1890ff;
}

/* 状态筛选 */
.status-filter {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.filter-tag {
  flex: 1;
  text-align: center;
  padding: 6px 0;
  border-radius: 16px;
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.3s;
}

.filter-tag.active {
  color: #fff;
  background: #1890ff;
}

.filter-tag.picked.active {
  background: #52c41a;
}

.filter-tag.pending.active {
  background: #fa8c16;
}

/* 提示文字 */
.tip-text {
  font-size: 11px;
  color: #999;
  margin-bottom: 8px;
}

/* 加载中 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #999;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #eee;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 空状态 */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
}

.empty-text {
  font-size: 13px;
  color: #999;
}

/* 卡片列表 */
.card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 快递卡片 */
.track-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 3px solid #1890ff;
  cursor: pointer;
}

.track-card:active {
  opacity: 0.9;
}

.track-card.picked {
  border-left-color: #52c41a;
}

.track-card.pending {
  border-left-color: #fa8c16;
}

/* 运输中：有轨迹但未签收（Status=0 为上传流程保留） */
.track-card.transit {
  border-left-color: #1890ff;
}

.track-card.error {
  border-left-color: #f5222d;
}

/* 卡片头部 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #f5f5f5;
}

.bill-no {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 4px;
}

.copy-icon {
  font-size: 12px;
  cursor: pointer;
}

.com-name-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 来源标识：销售出库=蓝、其他出库=橙 */
.source-tag {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.4;
  flex-shrink: 0;
}

.source-tag.sal {
  color: #1890ff;
  background: #e6f4ff;
}

.source-tag.stk {
  color: #fa8c16;
  background: #fff7e6;
}

/* 快递公司标识：按公司配色 */
.com-tag {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.4;
  white-space: nowrap;
  flex-shrink: 0;
}

.com-tag.c1 { color: #1890ff; background: #e6f4ff; }
.com-tag.c2 { color: #52c41a; background: #f6ffed; }
.com-tag.c3 { color: #fa8c16; background: #fff7e6; }
.com-tag.c4 { color: #722ed1; background: #f9f0ff; }
.com-tag.c5 { color: #eb2f96; background: #fff0f6; }
.com-tag.c6 { color: #13c2c2; background: #e6fffb; }

.com-name {
  font-size: 12px;
  color: #666;
}

/* 卡片主体 */
.card-body {
  padding: 6px 10px;
  text-align: left;
}

.status-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.status-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
}

.status-badge.success {
  color: #52c41a;
  background: #f6ffed;
}

.status-badge.warning {
  color: #fa8c16;
  background: #fff7e6;
}

.status-badge.info {
  color: #1890ff;
  background: #e6f4ff;
}

.status-badge.danger {
  color: #f5222d;
  background: #fff1f0;
}

.update-time {
  font-size: 11px;
  color: #999;
}

.remark {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  line-height: 1.3;
}

.bill-info,
.phone-info,
.sender-info {
  font-size: 12px;
  color: #666;
  margin-bottom: 2px;
}

.label {
  color: #999;
}

/* 卡片底部 */
.card-footer {
  padding: 4px 10px;
  border-top: 1px solid #f5f5f5;
  text-align: right;
}

.view-detail {
  font-size: 12px;
  color: #1890ff;
}
</style>
